import { useI18n } from "@wato/i18n/react";
import { Button, CloseIcon, Row, Stack } from "@wato/ui";
import { useUiLayer } from "../onboarding/useUiLayer";
import { useInstallPrompt } from "./useInstallPrompt";

/** WP-125: dismissible custom "Add to Home Screen" prompt. Renders nothing until the browser
 * fires `beforeinstallprompt` (captured early by `useInstallPrompt`), and stays hidden for good
 * once dismissed (UX-19, per user, server-side). iOS Safari never fires the event (no native
 * install API — `docs/runbooks/pwa-device-testing.md`): see `InstallAppMenuItem` for its hint. */
export function InstallPrompt({ workspaceId }: { readonly workspaceId: string }) {
  const { t } = useI18n();
  const { canInstall, install } = useInstallPrompt();
  const layer = useUiLayer(workspaceId, "pwaInstall");

  if (!canInstall || !layer.show) return null;

  const dismiss = layer.dismiss;

  return (
    <Row
      justify="between"
      className="border-b border-line bg-surface-muted px-4 py-2"
      role="region"
      aria-label={t("pwa.install.title")}
    >
      <Stack gap={0}>
        <p className="text-sm font-semibold text-fg">{t("pwa.install.title")}</p>
        <p className="text-xs text-fg-muted">{t("pwa.install.body")}</p>
      </Stack>
      <Row gap={2}>
        <Button size="sm" onClick={() => void install()}>
          {t("pwa.install.action")}
        </Button>
        <Button variant="ghost" size="sm" onClick={dismiss} aria-label={t("pwa.install.dismiss")}>
          <CloseIcon className="size-4" />
        </Button>
      </Row>
    </Row>
  );
}
